import React, { useEffect, useId, useRef, useState } from "react";
import Modal from "react-modal";
import { UserDetails, Education, Experience } from "../../types/Types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { CiTrash, CiCalendar } from "react-icons/ci";
import { IoAdd, IoAlertCircleOutline, IoClose } from "react-icons/io5";
import Button from "./Button";
import { ButtonTypes, instrumentChipSelectStyles } from "../../utils/Constants";

interface EditProfileModalProps {
  user: UserDetails | null;
  isOpen: boolean;
  instruments: { value: string; label: string }[];
  onRequestClose: () => void;
  onSave: (updatedUser: UserDetails, avatarFile?: File | null) => void;
  isSaving?: boolean;
}

const inputClass = (invalid = false) =>
  `w-full px-4 py-[9px] border rounded-full outline-none bg-white placeholder:text-[#9a9a9a] ${
    invalid
      ? "border-red-primary ring-1 ring-inset ring-red-primary"
      : "border-[#D9D9D9] focus:border-primary focus:ring-1 focus:ring-inset focus:ring-primary"
  }`;

const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="text-sm font-semibold text-gray-700">
    {children}
  </label>
);

const RequiredNote: React.FC<{ id: string }> = ({ id }) => (
  <span id={id} className="text-xs font-semibold text-red-primary">Required</span>
);

const GroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-bold tracking-[0.09em] uppercase text-[#526554]">{children}</h3>
);

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  user,
  isOpen,
  instruments,
  onRequestClose,
  onSave,
  isSaving
}) => {
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>(user?.instruments || []);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.profile_photo || null);
  // Only highlight missing fields after a save attempt, not while the user
  // is still filling in a freshly added entry.
  const [showErrors, setShowErrors] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  const fieldId = (name: string) => `${baseId}-${name}`;

  const defaultUser: UserDetails = {
    id: "",
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    bio: "",
    profile_photo: null,
    position: "",
    instruments: [],
    education: [],
    experiences: [],
  };

  const [formData, setFormData] = useState<UserDetails>({
    ...defaultUser,
    ...user,
    instruments: user?.instruments || [],
    education: user?.education || [],
    experiences: user?.experiences || [],
  });

  // This modal stays mounted permanently (only `isOpen` toggles), so without
  // this, formData/selectedInstruments/avatarPreview would only ever reflect
  // whatever `user` was on the very first mount. Concretely: save a new
  // education entry, and the entry that came back from the server with a
  // real id would still look id-less here — the next save would resubmit it
  // as "new" again, and editing it in between would leave the old copy
  // orphaned in the database as an apparent duplicate. Resyncing on every
  // open guarantees formData always reflects what the server actually has.
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      ...defaultUser,
      ...user,
      instruments: user?.instruments || [],
      education: user?.education || [],
      experiences: user?.experiences || [],
    });
    setSelectedInstruments(user?.instruments || []);
    setAvatarFile(null);
    setAvatarPreview(user?.profile_photo || null);
    setShowErrors(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEducationChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEducation = [...formData.education];
    newEducation[index] = {
      ...newEducation[index],
      [e.target.name]: e.target.value,
    };
    setFormData({ ...formData, education: newEducation });
  };

  const handleExperienceChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newExperiences = [...formData.experiences];
    newExperiences[index] = {
      ...newExperiences[index],
      [e.target.name]: e.target.value,
    };
    setFormData({ ...formData, experiences: newExperiences });
  };

  const handleAddEducation = () => {
    setFormData({
      ...formData,
      education: [
        ...formData.education,
        { degree: "", institution: "", start_date: null, end_date: null },
      ],
    });
  };

  const handleAddExperience = () => {
    setFormData({
      ...formData,
      experiences: [
        ...formData.experiences,
        { position: "", company: "", start_date: null, end_date: null },
      ],
    });
  };

  const handleRemoveEducation = (index: number) => {
    const newEducation = [...formData.education];
    newEducation.splice(index, 1);
    setFormData({ ...formData, education: newEducation });
  };

  const handleRemoveExperience = (index: number) => {
    const newExperiences = [...formData.experiences];
    newExperiences.splice(index, 1);
    setFormData({ ...formData, experiences: newExperiences });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setAvatarFile(f);
    setAvatarPreview(f ? URL.createObjectURL(f) : (user?.profile_photo || null));
  };

  const handleUndoPhoto = () => {
    setAvatarFile(null);
    setAvatarPreview(user?.profile_photo || null);
    // Clear the input too, so picking the same file again still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isEducationValid = formData.education.every(edu => edu.degree && edu.institution && edu.start_date && edu.end_date);
  const isExperienceValid = formData.experiences.every(exp => exp.position && exp.company && exp.start_date && exp.end_date);
  const isMissing = (value: string | null | undefined) => showErrors && !value;

  const handleSubmit = () => {
    if (!isEducationValid || !isExperienceValid) {
      setShowErrors(true);
      // Wait for the highlighted fields to render, then scroll to the first
      // one. Scrolled rather than focused: focusing a date field would pop
      // its calendar open on its own.
      requestAnimationFrame(() => {
        const firstInvalid = bodyRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
        firstInvalid?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
      return;
    }

    try {
      const updatedUser = {
        ...formData,
        instruments: selectedInstruments,
        education: formData.education?.map(edu => ({ ...edu, user: formData.id })) || [],
        experiences: formData.experiences?.map(exp => ({ ...exp, user: formData.id })) || [],
      };

      // onSave(updatedUser);
      onSave(updatedUser, avatarFile);
      onRequestClose();
    } catch (error) {
      console.error("Error updating user profile:", error);
    }
  };

  const handleDateChange = (
    date: Date | null,
    name: string,
    index: number,
    type: "education" | "experiences"
  ) => {
    const newData = [...formData[type]];
    newData[index] = {
      ...newData[index],
      [name]: date ? date.toISOString().split('T')[0] : null,
    };
    setFormData({ ...formData, [type]: newData });
  };

  const renderTextField = (
    id: string,
    label: string,
    name: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    placeholder: string,
    invalid = false
  ) => (
    <div className="flex flex-col gap-[5px] min-w-0">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-error` : undefined}
        className={inputClass(invalid)}
      />
      {invalid && <RequiredNote id={`${id}-error`} />}
    </div>
  );

  const renderDateField = (
    id: string,
    label: string,
    value: string | null,
    onChange: (date: Date | null) => void,
    invalid = false
  ) => (
    <div className="flex flex-col gap-[5px] min-w-0">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <DatePicker
          id={id}
          selected={value ? new Date(value) : null}
          onChange={onChange}
          dateFormat="yyyy-MM-dd"
          placeholderText="YYYY-MM-DD"
          ariaInvalid={invalid ? "true" : undefined}
          ariaDescribedBy={invalid ? `${id}-error` : undefined}
          wrapperClassName="w-full"
          className={`${inputClass(invalid)} pr-10`}
        />
        <CiCalendar className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xl text-gray-70 pointer-events-none" />
      </div>
      {invalid && <RequiredNote id={`${id}-error`} />}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Edit profile"
      className="w-[300px] 425:w-[400px] 600:w-[500px] 802:w-[700px] max-h-[88%] flex flex-col bg-white shadow-lg rounded-3xl overflow-hidden outline-none cursor-default"
      overlayClassName="fixed inset-0 flex backdrop items-center justify-center"
    >
      {/* Header and footer stay put; only the form in between scrolls. */}
      <div className="flex justify-between items-center gap-3 pl-6 pr-4 py-4 border-b border-gray-e8 shrink-0">
        <h2 className="text-xl font-bold">Edit profile</h2>
        <button
          type="button"
          onClick={onRequestClose}
          disabled={isSaving}
          aria-label="Close"
          className="w-9 h-9 rounded-full grid place-items-center text-2xl text-black41 hover:bg-gray-f6 disabled:opacity-50"
        >
          <IoClose />
        </button>
      </div>

      <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-6">
        {showErrors && (!isEducationValid || !isExperienceValid) && (
          <div className="flex gap-2.5 items-start bg-red-primary/[0.08] text-[#8a1515] rounded-2xl px-3.5 py-2.5 text-sm" role="alert">
            <IoAlertCircleOutline className="text-lg shrink-0 mt-px" />
            <span>Fill in the highlighted fields before saving.</span>
          </div>
        )}

        <section className="flex flex-col gap-3.5">
          <GroupTitle>Photo</GroupTitle>
          <div className="flex items-center gap-4 flex-wrap">
            <img
              src={avatarPreview || "/Images/UserProfile.png"}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover border bg-white shrink-0"
            />
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
                >
                  Change photo
                </button>
                {avatarFile && (
                  <span className="text-[13px] text-black41 min-w-0 break-all">
                    {avatarFile.name} ·{" "}
                    <button
                      type="button"
                      onClick={handleUndoPhoto}
                      className="text-primary font-semibold underline underline-offset-[3px]"
                    >
                      Undo
                    </button>
                  </span>
                )}
              </div>
              <span className="text-[13px] text-gray-70">A square photo works best.</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              aria-label="Profile photo"
            />
          </div>
        </section>

        <section className="flex flex-col gap-3.5">
          <GroupTitle>About you</GroupTitle>
          <div className="grid grid-cols-1 600:grid-cols-2 gap-3.5">
            {renderTextField(fieldId("first_name"), "First name", "first_name", formData.first_name, handleChange, "")}
            {renderTextField(fieldId("last_name"), "Last name", "last_name", formData.last_name, handleChange, "")}
          </div>
          {renderTextField(fieldId("position"), "Position", "position", formData.position, handleChange, "e.g. Session musician, music teacher")}
          <div className="flex flex-col gap-[5px]">
            <FieldLabel htmlFor={fieldId("bio")}>Bio</FieldLabel>
            <textarea
              id={fieldId("bio")}
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              placeholder="A few words about you and the music you make"
              className={`${inputClass()} !rounded-[20px] resize-y min-h-[84px]`}
              rows={3}
            ></textarea>
          </div>
          <div className="flex flex-col gap-[5px]">
            <FieldLabel htmlFor={fieldId("instruments")}>Instruments</FieldLabel>
            <Select
              isMulti
              inputId={fieldId("instruments")}
              name="instruments"
              options={instruments}
              classNamePrefix="select"
              value={instruments.filter(option => selectedInstruments.includes(option.value))}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions.map(option => option.value);
                setSelectedInstruments(selectedValues);
              }}
              styles={instrumentChipSelectStyles}
            />
          </div>
        </section>

        <section className="flex flex-col gap-3.5">
          <GroupTitle>Contact</GroupTitle>
          <div className="flex flex-col gap-[5px]">
            <FieldLabel htmlFor={fieldId("email")}>Email</FieldLabel>
            <input
              id={fieldId("email")}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              aria-describedby={fieldId("email-hint")}
              className={inputClass()}
            />
            <span id={fieldId("email-hint")} className="text-[13px] text-gray-70">
              Shown on your profile so other musicians can reach you.
            </span>
          </div>
        </section>

        <hr className="-mx-6 border-gray-e8" />

        <section className="flex flex-col gap-3.5">
          <GroupTitle>Education</GroupTitle>
          {formData.education.length === 0 && (
            <p className="text-sm text-gray-70">No education added yet.</p>
          )}
          {formData.education.map((edu: Education, index: number) => {
            const prefix = fieldId(`education-${index}`);
            const hasError = isMissing(edu.degree) || isMissing(edu.institution) || isMissing(edu.start_date) || isMissing(edu.end_date);
            return (
              <div
                // New entries have no id yet; fall back to the position so
                // React doesn't warn about duplicate keys.
                key={edu.id ?? `new-${index}`}
                className={`rounded-[18px] px-4 pt-3.5 pb-4 flex flex-col gap-3 ${hasError ? "bg-[#fff8f8] ring-[1.5px] ring-inset ring-red-primary/30" : "bg-gray-f6"}`}
              >
                <div className="flex justify-between items-center gap-2.5">
                  <span className="font-semibold text-[15px] truncate min-w-0">{edu.degree || "New education"}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveEducation(index)}
                    className="shrink-0 flex items-center gap-1 rounded-full px-2 py-1 text-[13px] font-semibold text-red-primary hover:bg-red-primary/[0.08]"
                  >
                    <CiTrash className="text-lg" />
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 600:grid-cols-2 gap-3.5">
                  {renderTextField(`${prefix}-degree`, "Degree", "degree", edu.degree, (e) => handleEducationChange(index, e), "e.g. BMus in Cello", isMissing(edu.degree))}
                  {renderTextField(`${prefix}-institution`, "School", "institution", edu.institution, (e) => handleEducationChange(index, e), "e.g. Royal Conservatory", isMissing(edu.institution))}
                </div>
                <div className="grid grid-cols-1 600:grid-cols-2 gap-3.5">
                  {renderDateField(`${prefix}-start`, "From", edu.start_date, (date) => handleDateChange(date, "start_date", index, "education"), isMissing(edu.start_date))}
                  {renderDateField(`${prefix}-end`, "To", edu.end_date, (date) => handleDateChange(date, "end_date", index, "education"), isMissing(edu.end_date))}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={handleAddEducation}
            className="w-full flex items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-primary/25 py-2.5 font-semibold text-primary hover:bg-primary/[0.04] hover:border-primary/40"
          >
            <IoAdd className="text-lg" />
            Add education
          </button>
        </section>

        <section className="flex flex-col gap-3.5">
          <GroupTitle>Experience</GroupTitle>
          {formData.experiences.length === 0 && (
            <p className="text-sm text-gray-70">No experience added yet.</p>
          )}
          {formData.experiences.map((exp: Experience, index: number) => {
            const prefix = fieldId(`experience-${index}`);
            const hasError = isMissing(exp.position) || isMissing(exp.company) || isMissing(exp.start_date) || isMissing(exp.end_date);
            return (
              <div
                key={exp.id ?? `new-${index}`}
                className={`rounded-[18px] px-4 pt-3.5 pb-4 flex flex-col gap-3 ${hasError ? "bg-[#fff8f8] ring-[1.5px] ring-inset ring-red-primary/30" : "bg-gray-f6"}`}
              >
                <div className="flex justify-between items-center gap-2.5">
                  <span className="font-semibold text-[15px] truncate min-w-0">{exp.position || "New experience"}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveExperience(index)}
                    className="shrink-0 flex items-center gap-1 rounded-full px-2 py-1 text-[13px] font-semibold text-red-primary hover:bg-red-primary/[0.08]"
                  >
                    <CiTrash className="text-lg" />
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 600:grid-cols-2 gap-3.5">
                  {renderTextField(`${prefix}-position`, "Role", "position", exp.position, (e) => handleExperienceChange(index, e), "e.g. Principal cellist", isMissing(exp.position))}
                  {renderTextField(`${prefix}-company`, "Band, orchestra or company", "company", exp.company, (e) => handleExperienceChange(index, e), "e.g. City Chamber Orchestra", isMissing(exp.company))}
                </div>
                <div className="grid grid-cols-1 600:grid-cols-2 gap-3.5">
                  {renderDateField(`${prefix}-start`, "From", exp.start_date, (date) => handleDateChange(date, "start_date", index, "experiences"), isMissing(exp.start_date))}
                  {renderDateField(`${prefix}-end`, "To", exp.end_date, (date) => handleDateChange(date, "end_date", index, "experiences"), isMissing(exp.end_date))}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={handleAddExperience}
            className="w-full flex items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-primary/25 py-2.5 font-semibold text-primary hover:bg-primary/[0.04] hover:border-primary/40"
          >
            <IoAdd className="text-lg" />
            Add experience
          </button>
        </section>
      </div>

      <div className="flex justify-end items-center gap-2.5 px-6 py-3.5 border-t border-gray-e8 bg-white shrink-0">
        <Button type={ButtonTypes.CANCEL} onClick={onRequestClose} className="!px-4 !py-2 font-semibold" disabled={isSaving}>
          Cancel
        </Button>
        <Button type={ButtonTypes.PRIMARY} onClick={() => handleSubmit()} className="!px-5 !py-2 font-semibold" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </Modal>
  );
};

export default EditProfileModal;
